import ReactJsonView, { InteractionProps } from "@microlink/react-json-view";
import * as React from "react";
import { Trans, useTranslation } from "react-i18next";

import style from "./launch-workflow-dialog.scss";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import buttons from "styles/buttons.scss";

import testIds from "testIds.json";

interface Props {
    visible: boolean;
    initialParameters?: Record<string, unknown>;
    onClose: () => void;
    onLaunch: (parameters: Record<string, unknown>) => Promise<void>;
}

const LaunchWorkflowDialog: React.FunctionComponent<Props> = (props) => {
    const { t } = useTranslation();
    const [parameters, setParameters] = React.useState<Record<string, unknown>>(
        props.initialParameters ? props.initialParameters : {}
    );
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (!props.visible) {
            setLoading(false);
        }
    }, [props.visible]);

    const handleJsonChange = (event: InteractionProps) => {
        setParameters(event.updated_src as Record<string, unknown>);
        return true;
    };

    const sendUsageStatistics = () => {
        usageStatisticsService.sendEvent({
            category: Category.WORKFLOW,
            action: Action.LAUNCH_WORKFLOW,
        });
    };

    const handleLaunch = () => {
        sendUsageStatistics();
        setLoading(true);
        props.onLaunch(parameters).finally(() => {
            setLoading(false);
        });
    };

    const handleLaunchAndClose = () => {
        sendUsageStatistics();
        setLoading(true);
        props.onLaunch(parameters).finally(() => {
            props.onClose();
        });
    };

    return (
        <Modal isOpen={props.visible} hideModal={props.onClose} modalTitle={t("LaunchWorkflow.title")}>
            {loading ? (
                <LoadingIndicator />
            ) : (
                <div className={style.container} data-testid={testIds.workArea.workflows.launchWorkflowDialog.itself}>
                    <div>{t("LaunchWorkflow.launchMessage")}</div>
                    <div className={style.warning}>
                        <Trans i18nKey="LaunchWorkflow.warning" />
                    </div>

                    <div
                        className={style.editorContainer}
                        data-testid={testIds.workArea.workflows.launchWorkflowDialog.parametersEditor}
                    >
                        <ReactJsonView
                            name={null}
                            src={parameters}
                            onAdd={handleJsonChange}
                            onEdit={handleJsonChange}
                            onDelete={handleJsonChange}
                        />
                    </div>
                    <div className={style.buttonContainer}>
                        <button
                            className={buttons.secondaryButtonWithoutIcon}
                            onClick={handleLaunch}
                            data-testid={testIds.workArea.workflows.launchWorkflowDialog.launchButton}
                        >
                            {t("LaunchWorkflow.launchButton")}
                        </button>
                        <button
                            className={buttons.primaryButtonWithoutIcon}
                            onClick={handleLaunchAndClose}
                            data-testid={testIds.workArea.workflows.launchWorkflowDialog.launchAndCloseButton}
                        >
                            {t("LaunchWorkflow.launchAndCloseButton")}
                        </button>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default LaunchWorkflowDialog;
